import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

export interface IQuickCreatedOrder {
    id: number
    order_code: string
    client_name: string
    project_name: string
    reference_paper_color: number
    printing_inks_amount: number
    general_info_programmed_kilograms: number
}

export interface IEntryDraft {
    project_name: string
    paper_color: number
    printing_inks_amount: number
    kilograms: number
    reference_number: number | null
    op_date: string | null
}

interface Props {
    open: boolean
    onClose: () => void
    parentClientName: string
    entryDraft: IEntryDraft
    onCreated: (order: IQuickCreatedOrder) => void
}

const csrfToken = () =>
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""

export default function NewOrderDialog({
    open, onClose, parentClientName, entryDraft, onCreated,
}: Props) {
    const { t } = useTranslation()
    const [orderCode, setOrderCode] = useState("")
    const [orderDate, setOrderDate] = useState<Dayjs | null>(dayjs())
    const [orderEntryDate, setOrderEntryDate] = useState<Dayjs | null>(dayjs())
    const [loadingSuggestion, setLoadingSuggestion] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) return
        setError(null)
        setOrderDate(dayjs())
        setOrderEntryDate(entryDraft.op_date ? dayjs(entryDraft.op_date) : dayjs())
        setLoadingSuggestion(true)
        const today = dayjs().format("YYYY-MM-DD")
        fetch(`/orders/new_order_code?date=${today}`, { headers: { Accept: "application/json" } })
            .then(r => r.json())
            .then(j => setOrderCode(j.order_code ?? ""))
            .catch(() => setOrderCode(""))
            .finally(() => setLoadingSuggestion(false))
    }, [open])

    const handleCreate = async () => {
        setSubmitting(true)
        setError(null)
        try {
            const res = await fetch("/orders/quick_create", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRF-Token": csrfToken(),
                },
                body: JSON.stringify({
                    order: {
                        order_code: orderCode,
                        client_name: parentClientName,
                        project_name: entryDraft.project_name,
                        order_date: orderDate?.format("YYYY-MM-DD") ?? null,
                        order_entry_date: orderEntryDate?.format("YYYY-MM-DD") ?? null,
                        reference_paper_color: entryDraft.paper_color,
                        printing_inks_amount: entryDraft.printing_inks_amount,
                        general_info_programmed_kilograms: entryDraft.kilograms,
                        order_references_attributes: entryDraft.reference_number
                            ? [{ reference_number: entryDraft.reference_number }]
                            : [],
                    },
                }),
            })
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                const codeErr = body?.errors?.order_code?.[0]
                setError(codeErr ? t("export.newOrderDialog.errorOrderCodeTaken") : `HTTP ${res.status}`)
                return
            }
            const body = await res.json()
            onCreated(body.order as IQuickCreatedOrder)
            onClose()
        } catch (err) {
            setError((err as Error).message)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t("export.newOrderDialog.title")}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <TextField
                        label={t("export.newOrderDialog.orderCode")}
                        value={orderCode}
                        onChange={e => setOrderCode(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={loadingSuggestion || submitting}
                    />
                    <DatePicker
                        label={t("export.newOrderDialog.orderDate")}
                        value={orderDate}
                        onChange={v => setOrderDate(v)}
                        format="DD-MM-YYYY"
                        disabled={submitting}
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                    <DatePicker
                        label={t("export.newOrderDialog.orderEntryDate")}
                        value={orderEntryDate}
                        onChange={v => setOrderEntryDate(v)}
                        format="DD-MM-YYYY"
                        disabled={submitting}
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {t("export.newOrderDialog.client")}: {parentClientName || "—"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("orders.project")}: {entryDraft.project_name || "—"}
                    </Typography>
                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" size="small" disabled={submitting}>
                    {t("common.cancel")}
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    size="small"
                    disabled={submitting || loadingSuggestion || !orderCode.trim()}
                >
                    {t("export.newOrderDialog.create")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
